import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { cloneDeep, debounce } from 'lodash';
import { TreeView } from '@mui/x-tree-view/TreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, CircularProgress, Divider, FormControlLabel, IconButton, Radio, Stack } from '@mui/material';
import type { ApiOrgSummary } from '@agunity/api-v4';

import AgButton from '../AgButton';
import AgTextField from '../AgTextField';
import { BreadCrumbsProps } from '../AgBreadCrumbs';
import AgTypography from '../../Styles/AgTypography';
import { AgColor } from '../../Styles/AgColor';

import { RenderTree } from '../../Utils/TreeUtils';
import { OrgPrefixUppercase } from '../../../Constants';
import { useAppDispatch } from '../../../redux/hooks';
import { setOrgTree } from '../../../redux/slices/global';
import { getSparseTreePath } from '../../../redux/slices/organizations';

interface ExtendedRenderTree extends RenderTree {
  hasCh?: boolean;
}
interface RichObjectTreeViewProps {
  onNodeSelect: Function;
  onNodeExpandIconClicked: Function;
  data: ExtendedRenderTree;
  dataTestId?: string;
  breadcrumbs: BreadCrumbsProps[];
  resetBreadCrumbs?: () => void,
  loadingId?: number;
}

export default function AgTreeView(
  {
    onNodeSelect,
    onNodeExpandIconClicked,
    data,
    breadcrumbs,
    resetBreadCrumbs,
    loadingId
  }: RichObjectTreeViewProps) {
  const messages = useIntl().formatMessage;
  const dispatch = useAppDispatch();
  const [search, setSearch] = useState('');
  const [orgData, setOrgData] = useState<ExtendedRenderTree>(data);
  const [expandNodeId, setExpandNodeId] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = useCallback((event: React.SyntheticEvent, checked: boolean) => {
    if (checked && event.target instanceof HTMLInputElement) {
      // setSelectedItem(event.target.value);
      if (onNodeSelect) {
        onNodeSelect(event.target.value);
      }
    }
  }, [onNodeSelect]);

  const handleIconClick = useCallback((nodeId: string) => {
    if (expandNodeId.includes(nodeId)) {
      setExpandNodeId(prevState => prevState.filter(item => item !== nodeId));
    } else {
      setExpandNodeId(prevState => [...prevState, nodeId]);
      if (onNodeExpandIconClicked) {
        onNodeExpandIconClicked(nodeId);
      }
    }
  }, [expandNodeId, onNodeExpandIconClicked]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const filteredNodes = useCallback((node: ExtendedRenderTree, term: string): ExtendedRenderTree[] => {
    if (term.toUpperCase().startsWith(OrgPrefixUppercase) && search.length > 2) term = term.substring(2); // orgId may start with '#c'
    if (node.id.toLowerCase().includes(term.toLowerCase())) {
      return [node];
    }

    if (Array.isArray(node.children)) {
      const children = node.children.flatMap((child) =>
        filteredNodes(child, term)
      );
      if (children.length) {
        return [{ ...node, children }];
      }
    }

    return [];
  }, [search.length]);

  const renderTree = useCallback((node: ExtendedRenderTree) => {
    return(
      <TreeItem
        key={node.id}
        itemId={node.id}
        sx={{
          '& .MuiTreeItem-content': {
            padding: '8px 0'
          }
        }}
        label={
          <Box
            display='flex'
            flexDirection='row'
            justifyContent='space-between'
            alignItems='center'
            width='100%'
            sx={{
              color: AgColor.secondaryDark
            }}
          >
            <FormControlLabel
              value={node.id}
              control={<Radio color='default' style={{ color: AgColor.secondaryDark }} />}
              label={
                <Box>
                  <div>{node.name}</div>
                  <span style={{ fontSize: '0.8rem' }}>#c{node.id}</span>
                </Box>
              }
              onChange={handleChange}
              checked={breadcrumbs.some(x => x.name === node.name)}
            />
            {(node.children?.length || node.hasCh) ? (
              <IconButton onClick={() => loadingId === Number(node.id) ? null : handleIconClick(node.id)} size='small' sx={{ color: AgColor.secondaryDark }}>
                {
                  (loadingId !== 0 && loadingId === Number(node.id)) ?
                    <CircularProgress color='inherit' size='1rem' sx={{ p: .5 }} /> :
                    <ExpandMoreIcon />
                }
              </IconButton>
            ) : null}
          </Box>
        }
      >
        {Array.isArray(node.children) || node.hasCh
          ? (node.children || []).map((child) => renderTree(child))
          : null}
      </TreeItem>
    );
  }, [breadcrumbs, handleChange, handleIconClick, loadingId]);

  const addSearchedTreePath = (treePathArray: ApiOrgSummary[]) => {
    let treePath = treePathArray;
    let updatedOrgData = cloneDeep(orgData);
  
    if (treePath[0].id?.toString() === updatedOrgData.id) {
      // treePath is in root to leaf order
    } else if (treePath[treePath.length - 1].id?.toString() === updatedOrgData.id) {
      // treePath is in leaf to root order, reverse it
      treePath = treePath.slice().reverse();
    } else {
      // The root node is not in the treePath
      return;
    }
  
    let currentNode = updatedOrgData;
  
    for (let i = 1; i < treePath.length; i++) {
      const nodeId = treePath[i].id?.toString();
      let childNode = currentNode.children?.find(child => child.id === nodeId);
  
      if (!childNode) {
        // If the child does not exist, create it
        childNode = {
          id: nodeId ?? '',
          name: treePath[i].name ?? '',
          children: []
        };
        currentNode.children = currentNode.children || [];
        currentNode.children.push(childNode);
      }
      // Move to the next node in the path
      currentNode = childNode;
    }
  
    setOrgData(updatedOrgData);
    dispatch(setOrgTree(updatedOrgData));
  };

  const addExpandItemIdFromTreePath = (treePathArray: ApiOrgSummary[], search: string) => {
    // searched node will not expand, besides, all other parent nodes are expanded
    const nodeIdArray = treePathArray.filter(item => item.id?.toString() !== search).map(tree => tree.id?.toString() || '') || [];
    setExpandNodeId(nodeIdArray);
  };

  useEffect(()=>{
    if (!search) {
      setIsLoading(false);
      return;
    };
    setIsLoading(true);
    const getTreePath = async (search: string) => {
      try {
        if (search.toUpperCase().startsWith(OrgPrefixUppercase) && search.length > 2) search = search.substring(2);  // orgId may start with '#c'
        const res = await dispatch(getSparseTreePath(Number(search)));
        if (res?.data?.length) {
          addExpandItemIdFromTreePath(res.data, search);
          addSearchedTreePath(res.data);
        }
      }catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      } finally {
        setIsLoading(false);
      }
    };
    const debounceGetTreePath = debounce(getTreePath, 1000);

    debounceGetTreePath(search); // debounce, otherwise it will call API multiple times with a short period of time

    return () => {
      debounceGetTreePath.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[dispatch, search]);

  useEffect(() => {
    const reconstructChildren = (data?.children || []).map(item => {
      return item.id === 'select' ? { ...item, id: '', name: messages({ id: 'SELECT' }) } : item;
    });

    const formattedData = { ...data, children: reconstructChildren };
    formattedData && setOrgData(formattedData);
  }, [data, messages]);

  const treeList = useMemo(() => {
    return orgData?.children
      ?.flatMap((child) => filteredNodes(child, search))
      .map((item) => renderTree(item));
  }, [filteredNodes, orgData?.children, renderTree, search]);

  const handleReset = useCallback(() => {
    resetBreadCrumbs?.();
    setSearch('');
  }, [resetBreadCrumbs]);

  const Loader = memo(() => {
    return (
      <Stack direction='row' gap={1} justifyContent='center' alignItems='center' width='100%' py={1}>
        <CircularProgress size={32} />
        <AgTypography contentStyle='AP.H4.Bold' message={messages({ id: 'LOADING' })} sx={{ color: AgColor.primaryDark }} />
      </Stack>
    );
  });

  return (
    <Stack gap={1} sx={{ minHeight: 180 }}>
      <Box sx={{ justifyContent: 'space-between', display: 'flex', flexDirection: 'column', padding: '0 12px', mb: 2 }}>
        <AgButton onClick={handleReset} variant='outlined' label={messages({ id: 'CLEAR' })} style={{ textDecoration: 'underline', textUnderlineOffset: 3, width: '24px', border: 0, textTransform: 'capitalize' }}/>
        <AgTextField
          placeholder={messages({ id: 'SEARCH_BY_ORGANIZATION_ID' })}
          value={search}
          onChange={handleSearchChange}
          style={{ height: '38px' }}
        />
      </Box>
      <Divider />
      <Box sx={{ height: '100%', maxHeight: 'calc(100vh - 200px)', overflowY: 'auto', mb: 1 }}>
        {isLoading ? (
          <Loader />
        ) : !!treeList?.length ? (
          <TreeView expandedItems={expandNodeId} style={{ marginTop: '16px' }}>
            {treeList}
          </TreeView>
        ) : (
          <AgTypography
            contentStyle='AP.H4.Medium'
            message={messages({ id: 'NO_SEARCH_RESULTS' })}
            sx={{ color: AgColor.grey7, p: '16px 12px' }}
          />
        )}
      </Box>
    </Stack>
  );
}
